import { Typography, Box, Button, Popover, useTheme, CircularProgress, Stack } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import PersonIcon from '@mui/icons-material/Person'
import { useState } from 'react'
import { useBatchDeleteLeads } from 'api/aws/batch/useBatchDeleteLeads'

type Lead = { lead_uuid?: string; id?: string; name?: string }

const LeadsPopover = ({
  id,
  open,
  anchorEl,
  onClose,
  handleConfirmDelete, // () => Promise<void> | void (single)
  selectedData, // Lead | null (single)
  selectedLeads = [], // Lead[] (multi) – pass [] when single
  selectionCount = 0,
  selectionIds = [],
}: {
  id: string
  open: boolean
  anchorEl?: HTMLElement | null
  onClose: () => void | Promise<void>
  handleConfirmDelete: () => void | Promise<void>
  selectedData?: Lead | null
  selectedLeads?: Lead[]
  selectionCount?: number
  selectionIds?: (string | number)[]
}) => {
  const theme = useTheme()
  const [isDeleting, setIsDeleting] = useState(false)
  const { mutateAsync: batchDelete } = useBatchDeleteLeads()

  const handleSingleDelete = async () => {
    setIsDeleting(true)
    try {
      await Promise.resolve(handleConfirmDelete())
      await Promise.resolve(onClose())
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBatchDelete = async () => {
    setIsDeleting(true)
    try {
      // Prefer ids from props; fallback to mapping from objects
      const ids = (selectionIds?.length ? selectionIds : selectedLeads.map(l => l.lead_uuid || l.id)).filter(
        Boolean
      ) as string[]
      console.log('ids', ids)
      if (!ids.length) throw new Error('No lead IDs to delete.')
      await batchDelete({ ids }) // IMPORTANT: object payload { ids }
      await Promise.resolve(onClose())
    } finally {
      setIsDeleting(false)
    }
  }

  const isMulti = selectionCount > 1

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
      transformOrigin={{ vertical: 'center', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 400,
            maxWidth: 500,
          },
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <WarningIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.error.main,
            }}
          >
            Confirm Deletion
          </Typography>
        </Box>

        {/* Message */}
        <Typography
          sx={{
            mb: 3,
            color: theme.palette.text.primary,
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Are you sure you want to permanently delete the following {isMulti ? 'leads' : 'lead'}?
        </Typography>

        {/* Single lead preview */}
        {!isMulti && selectedData && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1.5,
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.error.main}40`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <PersonIcon
              sx={{
                color: theme.palette.primary.main,
                fontSize: 32,
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '1rem',
              }}
            >
              {selectedData?.name || 'Unnamed Lead'}
            </Typography>
          </Box>
        )}

        {/* Multi leads preview */}
        {isMulti && selectedLeads?.length > 0 && (
          <Stack
            spacing={1}
            sx={{
              mb: 3,
              maxHeight: 200,
              overflowY: 'auto',
              p: 2,
              borderRadius: 1.5,
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.error.main}40`,
            }}
          >
            {selectedLeads.map(l => (
              <Box
                key={String(l.lead_uuid || l.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 0.5,
                }}
              >
                <PersonIcon
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: 24,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                  }}
                >
                  {l?.name || 'Unnamed Lead'}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {/* Action buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          {isDeleting && <CircularProgress size={24} sx={{ mr: 1 }} />}
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isDeleting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 90,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={isMulti ? handleBatchDelete : handleSingleDelete}
            disabled={isDeleting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 90,
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Box>
    </Popover>
  )
}

export default LeadsPopover
